import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { SyncPill } from "@/components/SyncPill";
import { RecordFormModal } from "@/components/RecordFormModal";
import { StatusChip } from "@/components/StatusChip";
import { EmptyState } from "@/components/EmptyState";
import { TableSkeleton } from "@/components/Skeletons";
import { Switch } from "@/components/ui/switch";
import { Plus, Star, MapPin, Film } from "lucide-react";

// Design System 2.0 (2026-10, Phase 5) — retokened off hardcoded hex; renamed "Production
// Partners" -> "Partners" (page title only, entity/route/scope names unchanged). Zero
// project-count now plain "0" (no Film icon) — the icon is reserved for a real count.
// Same list-page shape as Clients.jsx's ClientsList (search/table/New button/archived
// toggle), scoped to `partner_network`. Rate cards stay on their own dedicated
// /admin/partner-rate-cards queue, never duplicated here.
export default function Partners() {
  const { hasScope } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const load = (archived = includeArchived) => api.get(`/v1/partners/?include_archived=${archived}`)
    .then((r) => { setItems(r.data.items || []); setLoading(false); });
  useEffect(() => { load(includeArchived); }, [includeArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-8" data-testid="partners-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-editorial text-4xl tracking-tight text-ink">Partners</h1>
        <div className="flex items-center gap-4">
          <label className="inline-flex items-center gap-2 text-xs text-ink-soft">
            <Switch checked={includeArchived} onCheckedChange={setIncludeArchived} data-testid="partners-show-archived-toggle" />
            Show archived
          </label>
          {hasScope("partners.create") && (
            <button type="button" data-testid="new-partner-btn" onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-ink hover:bg-brand-dim">
              <Plus className="h-3.5 w-3.5" /> New Partner
            </button>
          )}
        </div>
      </div>

      <RecordFormModal type="partner" open={showNew} onClose={() => setShowNew(false)}
        onCreated={(record, opts) => { setShowNew(false); load(); if (opts?.openDetail && record?.id) navigate(`/partners/${record.id}`); }} />

      {loading ? <TableSkeleton cols={7} testId="partners-skeleton" /> : items.length === 0 ? (
        <div className="rounded-lg border border-hairline bg-paper">
          <EmptyState title="No production partners yet" hint="Add a partner to get started." testId="partners-empty"
            actionLabel={hasScope("partners.create") ? "+ New Partner" : undefined} onAction={hasScope("partners.create") ? () => setShowNew(true) : undefined} />
        </div>
      ) : (
        <div className="rounded-lg border border-hairline bg-paper overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline text-[10px] font-semibold uppercase tracking-wider text-faint">
                <th className="text-left px-4 py-3">Partner</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Geography</th>
                <th className="text-left px-4 py-3">Currency</th>
                <th className="text-right px-4 py-3">Projects</th>
                <th className="text-left px-4 py-3">Location</th>
                <th className="text-right px-4 py-3">Sync</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className={`border-b border-hairline last:border-b-0 hover:bg-surface2 ${p.archived ? "opacity-60" : ""}`}>
                  <td className="px-4 py-3">
                    <Link to={`/partners/${p.id}`} data-testid={`partner-row-${p.id}`} className="text-ink hover:text-gold-text inline-flex items-center gap-1.5">
                      {p.preferred_partner && <Star className="h-3 w-3 fill-brand text-brand" data-testid={`partner-preferred-star-${p.id}`} />}
                      {p.name}
                    </Link>
                    {p.archived && <span className="ml-2 rounded-full bg-tone-grey-bg px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-tone-grey-text" data-testid={`partner-archived-badge-${p.id}`}>Archived</span>}
                  </td>
                  <td className="px-4 py-3"><StatusChip status={p.status} testId={`partner-status-${p.id}`} /></td>
                  <td className="px-4 py-3 text-ink-soft" data-testid={`partner-type-${p.id}`}>{p.partner_type || "—"}</td>
                  <td className="px-4 py-3 text-ink-soft" data-testid={`partner-geography-${p.id}`}>{p.partner_geography || "—"}</td>
                  <td className="px-4 py-3 text-ink-soft" data-testid={`partner-currency-${p.id}`}>{p.default_currency || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-ink-soft" data-testid={`partner-project-count-${p.id}`}>
                    {p.project_count ? <><Film className="h-3 w-3 inline mr-1 opacity-50" />{p.project_count}</> : "0"}
                  </td>
                  <td className="px-4 py-3 text-ink-soft" data-testid={`partner-location-${p.id}`}>{[p.city, p.country].filter(Boolean).join(", ") || <span className="inline-flex items-center gap-1"><MapPin className="h-2.5 w-2.5" />—</span>}</td>
                  <td className="px-4 py-3 text-right"><SyncPill state={p.sync?.state} lastSyncedAt={p.sync?.last_synced_at} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
